import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../../css/header.css";
import { Context as AuthContext } from "./../../context/AuthContext";
import Cookies from "js-cookie";
import { axiosAuth } from "../../utils/axiosInstance";

const IMG_URL = process.env.REACT_APP_IMG_URL;

const getInitials = (name) => {
  if (!name?.trim()) return "U";
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const UserProfileMenu = ({
  user,
  onNavigate,
  onLogout,
  variant = "bar",
  showLogout = true,
}) => {
  const displayName = user?.name?.trim() || "User";
  const displayEmail = user?.email || user?.contact || user?.phone || "";
  const avatarSrc = user?.image
    ? user.image.startsWith("http")
      ? user.image
      : `${IMG_URL}${user.image}`
    : null;

  const menuItems = [
    { label: "Dashboard", path: "/dashboard", icon: "fa-gauge-high" },
    { label: "My Profile", path: "/profile", icon: "fa-user" },
    { label: "My Bookings", path: "/booking", icon: "fa-calendar-check" },
  ];

  return (
    <div className={`bv-user-menu bv-user-menu--${variant}`}>
      <div className="bv-user-menu__head">
        <span className="bv-user-menu__avatar" aria-hidden="true">
          {avatarSrc ? (
            <img src={avatarSrc} alt="" />
          ) : (
            <span>{getInitials(displayName)}</span>
          )}
        </span>
        <div className="bv-user-menu__meta">
          <strong>{displayName}</strong>
          {displayEmail ? <span>{displayEmail}</span> : null}
        </div>
      </div>

      <div className="bv-user-menu__links">
        {menuItems.map((item) => (
          <button
            key={item.path}
            type="button"
            className="bv-user-menu__link"
            onClick={() => onNavigate(item.path)}
          >
            <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
            <span>{item.label}</span>
            <i
              className="fa-solid fa-chevron-right bv-user-menu__link-arrow"
              aria-hidden="true"
            ></i>
          </button>
        ))}
      </div>

      {showLogout ? (
        <button
          type="button"
          className="bv-user-menu__logout"
          onClick={onLogout}
        >
          <i className="fa-solid fa-right-from-bracket" aria-hidden="true"></i>
          <span>Logout</span>
        </button>
      ) : null}
    </div>
  );
};

export const Header = () => {
  const { state, logout, openLoginModal } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const userMenuRef = useRef(null);

  const isAuthed = !!state.userData && !!Cookies.get("USER_TOKEN");

  useEffect(() => {
    setMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthed) {
      setUserProfile(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await axiosAuth.get("get-user-details");
        if (!cancelled && response.status === 200 && response.data?.user) {
          setUserProfile(response.data.user);
        }
      } catch (error) {
        if (!cancelled) console.error("Error fetching user profile:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  useEffect(() => {
    if (!userMenuOpen) return;

    const onPointerDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setUserMenuOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [userMenuOpen]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    setMenuOpen(false);
    logout(navigate);
  };

  const handleUserNavigate = (path) => {
    setUserMenuOpen(false);
    setMenuOpen(false);
    navigate(path);
  };

  const navItems = [
    { label: "Home", path: "/", icon: "fa-house" },
    { label: "Facilities", path: "/all-facilities", icon: "fa-futbol" },
    { label: "Hotels", path: "/hotels", icon: "fa-hotel" },
    // { label: "Tournaments", path: "/tournaments", icon: "fa-trophy" },
  ];

  const displayName = userProfile?.name?.trim() || "User";
  const avatarSrc = userProfile?.image
    ? userProfile.image.startsWith("http")
      ? userProfile.image
      : `${IMG_URL}${userProfile.image}`
    : null;

  const renderNavLinks = (linkClass = "bv-nav-link") =>
    navItems.map((item) => (
      <button
        key={item.label}
        type="button"
        className={`${linkClass} ${
          location.pathname === item.path ? "active" : ""
        }`}
        onClick={() => {
          setMenuOpen(false);
          navigate(item.path);
        }}
      >
        <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
        <span>{item.label}</span>
      </button>
    ));

  return (
    <header className="bv-site-nav-wrap">
      <div className="bv-site-nav">
        <div className="bv-site-nav__start">
          <nav className="bv-site-nav__desk" aria-label="Main navigation">
            {renderNavLinks()}
          </nav>
        </div>

        <div className="bv-site-nav__center">
          <button
            type="button"
            className="bv-brand"
            onClick={() => navigate("/")}
          >
            <img
              className="bv-brand__logo"
              src="/final-bv-logo.png"
              alt="BookVenue"
            />
          </button>
        </div>

        <div className="bv-site-nav__end">
          <div className="bv-site-nav__actions">
            {isAuthed ? (
              <div className="bv-user-dropdown" ref={userMenuRef}>
                <button
                  type="button"
                  className={`bv-user-trigger ${userMenuOpen ? "is-open" : ""}`}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                  onClick={() => setUserMenuOpen((v) => !v)}
                >
                  <span className="bv-user-trigger__avatar" aria-hidden="true">
                    {avatarSrc ? (
                      <img src={avatarSrc} alt="" />
                    ) : (
                      <span>{getInitials(displayName)}</span>
                    )}
                  </span>
                  <span className="bv-user-trigger__text">{displayName}</span>
                  <i
                    className="fa-solid fa-chevron-down bv-user-trigger__chev"
                    aria-hidden="true"
                  ></i>
                </button>

                {userMenuOpen && (
                  <div className="bv-user-dropdown__panel">
                    <UserProfileMenu
                      user={userProfile}
                      variant="bar"
                      onNavigate={handleUserNavigate}
                      onLogout={handleLogout}
                    />
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="bv-login-btn bv-login-btn--bar"
                onClick={() => openLoginModal()}
              >
                <span className="bv-login-btn__icon">
                  <i className="fa-regular fa-user"></i>
                </span>
                <span className="bv-login-btn__label">Login</span>
              </button>
            )}
            <Link
              className="bv-list-btn bv-list-btn--bar"
              to="https://admin.bookvenue.app/register"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="bv-list-btn__icon">
                <i className="fa-solid fa-plus"></i>
              </span>
              <span className="bv-list-btn__text">List Your Facility</span>
            </Link>
            <button
              type="button"
              className="bv-mobile-toggle"
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <i
                className={`fa-solid ${menuOpen ? "fa-xmark" : "fa-bars"}`}
              ></i>
            </button>
          </div>
        </div>

        <nav
          className={`bv-site-nav__drawer ${menuOpen ? "is-open" : ""}`}
          aria-label="Mobile menu"
        >
          <div className="bv-site-nav__drawer-links">{renderNavLinks()}</div>
          <div className="bv-site-nav__drawer-actions">
            {isAuthed ? (
              <UserProfileMenu
                user={userProfile}
                variant="mobile"
                showLogout={false}
                onNavigate={handleUserNavigate}
                onLogout={handleLogout}
              />
            ) : null}
            <div className="bv-site-nav__drawer-footer">
              {isAuthed ? (
                <button
                  type="button"
                  className="bv-drawer-footer-btn bv-drawer-footer-btn--logout"
                  onClick={handleLogout}
                >
                  <i
                    className="fa-solid fa-right-from-bracket"
                    aria-hidden="true"
                  />
                  <span>Logout</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="bv-drawer-footer-btn bv-drawer-footer-btn--login"
                  onClick={() => {
                    setMenuOpen(false);
                    openLoginModal();
                  }}
                >
                  <i className="fa-regular fa-user" aria-hidden="true" />
                  <span>Login</span>
                </button>
              )}
              <Link
                className="bv-drawer-footer-btn bv-drawer-footer-btn--list"
                to="https://admin.bookvenue.app/createFacility"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
              >
                <i className="fa-solid fa-plus" aria-hidden="true" />
                <span>List Court</span>
              </Link>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};
